// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { EllipsisTypography } from '@/components/ellipsis-typography';
import { FilterMenuButton } from '@/components/filter-menu-button';
import { FilterMenuItem } from '@/components/filter-menu-item';
import { ScrollIndicator } from '@/components/scroll-indicator';
import {
  CloseRounded,
  SearchRounded,
} from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useScrollIndicator } from '@/hooks/use-scroll-indicator';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import { debounce } from 'lodash-es';
import {
  KeyboardEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// Seeds the first frame only: every row reports its own height once it mounts,
// so this never has to be right, just close enough to size the scrollbar
const ESTIMATED_ROW_HEIGHT_PX = 32;
const DIVIDER_ITEM_HEIGHT_PX = 17;
const DENSE_ROW_MIN_HEIGHT_PX = 32;
const DEFAULT_MAX_MENU_HEIGHT_PX = 400;
const DEFAULT_MIN_MENU_WIDTH_PX = 400;
const SEARCH_DEBOUNCE_MS = 150;
const SKELETON_ROW_COUNT = 3;

// How far past the visible window to keep rows mounted, measured in viewports.
// A flick can scroll further than the next render covers, and the uncovered
// strip paints as blank — so the buffer is sized in viewports rather than a
// flat row count, which keeps it honest whatever the rows and the menu measure.
//
// Rendering a row is the single most expensive thing an open does, so the first
// paint covers the visible area only and the buffer follows once the menu is
// up. Nobody can flick a menu that is still animating, and the overflow rows
// arrive before they can.
const OVERSCAN_VIEWPORTS = 2;
const OVERSCAN_VIEWPORTS_WHILE_OPENING = 0;

// MUI's menu moves focus between items on these, which pulls the caret out of
// the search box mid-typing
const MENU_NAVIGATION_KEYS = ['ArrowUp', 'ArrowDown', 'Home', 'End'];

/**
 * A selectable option in a checkbox filter menu.
 *
 * Rows are identified by their `key`, not by anything in here — the same
 * domain entity can legitimately appear twice (a site and its "Default" zone
 * select the same location), so identity is the caller's to declare.
 */
interface FilterMenuOption {
  label: string;
  isActive: boolean;
  isIndeterminate?: boolean;

  /**
   * Text colour, using MUI's colour system.
   */
  color?: string;

  /**
   * Explanatory text shown in a tooltip beside the label.
   */
  example?: string;
}

/**
 * A single flattened row of the menu. Callers expand their own tree or group
 * structure into a linear list of these so the menu can render only the
 * visible slice.
 *
 * Every row measures itself, so rows of the same kind are free to differ in
 * height — a chip, an "Archived" suffix or a heading weight all size correctly
 * without the caller declaring anything.
 *
 * Two of these render a group heading, and they are not interchangeable: use
 * `header` for a heading the user cannot select, and an `isEmphasized` option
 * for one that carries its own checkbox.
 */
export type FilterMenuRow =
  | { kind: 'divider'; key: string }
  | { kind: 'message'; key: string; text: string }
  | {
      kind: 'header';
      key: string;
      label: string;

      /**
       * Indentation depth, matching an option row's. Zero is the top level.
       */
      level?: number;
    }
  | (FilterMenuOption & {
      kind: 'option';
      key: string;

      /**
       * Indentation depth. Zero is the top level.
       */
      level?: number;

      /**
       * A badge rendered after the label, such as "Inventory".
       */
      chip?: string;

      /**
       * Marks the row as archived, which appends a muted "Archived" suffix.
       */
      isArchived?: boolean;

      /**
       * Renders the label as a heading while keeping it selectable.
       */
      isEmphasized?: boolean;

      onChange: (isActive: boolean) => void;
    });

interface Props {
  /**
   * The label for the filter menu button.
   */
  label: string;

  /**
   * The number of active selections, shown as a chip on the button.
   */
  count: number;

  /**
   * Placeholder text for the search field.
   */
  searchPlaceholder: string;

  /**
   * Whether the options are still being fetched. An unfetched list is empty
   * for a different reason than a fetched one, so this replaces the list
   * rather than letting `getRows` describe it.
   */
  isLoading?: boolean;

  /**
   * Whether to show the search field.
   *
   * @default true
   */
  isSearchEnabled?: boolean;

  /**
   * The minimum width of the menu.
   *
   * @default 400
   */
  minWidth?: number;

  /**
   * The maximum height of the scrolling list.
   *
   * @default 400
   */
  maxHeight?: number;

  /**
   * Describes the list the floating scroll-to-bottom control jumps through.
   * Supplying it is what turns the control on, so it cannot render unnamed. It
   * earns its keep on a deeply nested list and is noise on a flat one.
   */
  scrollIndicatorLabel?: string;

  /**
   * Builds the flattened rows for the current search term, including the
   * message row for an empty list — the menu has no second empty state of its
   * own, so whatever this returns is what the user sees.
   *
   * Memoize it on the option data and the selection. Its identity is what
   * rebuilds the row list, so a callback that changes every render rebuilds
   * every row every render.
   */
  getRows: (searchTerm: string) => FilterMenuRow[];
}

/**
 * One rendered row.
 *
 * Memoized because the virtualizer re-renders on every notify — measuring,
 * attaching, scrolling — while the rows themselves are unchanged. Without
 * this an open re-renders every visible row about five times over, and a row
 * is the most expensive thing the menu draws.
 */
const FilterMenuRowContent = memo(function FilterMenuRowContent(props: {
  row: FilterMenuRow;
}) {
  if (props.row.kind === 'divider') {
    return <Divider sx={{ marginY: 1 }} />;
  }

  if (props.row.kind === 'message') {
    return (
      <MenuItem dense disabled>
        <Typography variant="body2" color="text.secondary">
          {props.row.text}
        </Typography>
      </MenuItem>
    );
  }

  // Not a MenuItem: that reports role="menuitem" aria-disabled, announcing a
  // heading as a command the user is not allowed to invoke. The padding
  // mirrors a dense MenuItem so headers sit on the same rhythm as the options
  // they head.
  if (props.row.kind === 'header') {
    return (
      <Box
        sx={{
          minHeight: DENSE_ROW_MIN_HEIGHT_PX,
          paddingX: 2,
          // Steps with an option row's, so a heading sits over the rows it heads
          paddingLeft: 2 + (props.row.level ?? 0) * 2,
          paddingY: 0.5,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <EllipsisTypography
          component="span"
          variant="body2"
          fontWeight={500}
          maximumWidth="100%"
          minWidth={0}
        >
          {props.row.label}
        </EllipsisTypography>
      </Box>
    );
  }

  return (
    <FilterMenuItem
      level={props.row.level}
      isActive={props.row.isActive}
      isIndeterminate={props.row.isIndeterminate}
      color={props.row.color}
      example={props.row.example}
      onChange={props.row.onChange}
    >
      <Stack direction="row" alignItems="center" minWidth={0}>
        {/* Rendered as a span because subtitle2 maps to h6, which would put a
            heading inside the checkbox label and into heading navigation */}
        <EllipsisTypography
          component="span"
          variant={props.row.isEmphasized ? 'subtitle2' : 'body2'}
          maximumWidth="100%"
          minWidth={0}
        >
          {props.row.label}
        </EllipsisTypography>
        {props.row.isArchived && (
          <Typography
            component="span"
            variant="body2"
            color="text.secondary"
            sx={{
              textTransform: 'lowercase',
              fontVariant: 'small-caps',
              marginLeft: 1,
              flexShrink: 0,
            }}
          >
            Archived
          </Typography>
        )}
        {props.row.chip !== undefined && (
          <Chip
            size="small"
            label={props.row.chip}
            sx={{ marginLeft: 1, flexShrink: 0 }}
          />
        )}
      </Stack>
    </FilterMenuItem>
  );
});

/**
 * The floating scroll-to-bottom control, mounted only when a caller names it.
 *
 * It lives in its own component so the observers behind `useScrollIndicator`
 * are installed only for the menus that show the control, rather than for
 * every filter menu in the app.
 */
function FilterMenuScrollIndicator(props: {
  element: HTMLDivElement | null;
  label: string;
  onScrollToEnd: () => void;
}) {
  const scrollIndicator = useScrollIndicator();
  const attach = scrollIndicator.ref;

  // The hook owns the element it watches, and this one is already attached by
  // the list above, so it is handed over rather than reffed
  useEffect(() => {
    attach(props.element);
  }, [attach, props.element]);

  const handleScrollToBottom = () => {
    props.onScrollToEnd();
    scrollIndicator.hide();
  };

  return (
    <ScrollIndicator
      ariaLabel={props.label}
      isVisible={scrollIndicator.isVisible}
      onClick={handleScrollToBottom}
    />
  );
}

interface ContentProps {
  searchPlaceholder: string;
  isSearchEnabled: boolean;
  scrollIndicatorLabel?: string;
  isLoading?: boolean;
  isOpen: boolean;
  isFullyOpen: boolean;
  minWidth: number;
  maxHeight: number;
  getRows: (searchTerm: string) => FilterMenuRow[];
}

/**
 * The menu's contents, mounted with the open menu and unmounted with it.
 *
 * Scoping the virtualizer to the open menu is what keeps this simple: an
 * instance that outlives its scroll container has to be told the container went
 * away, told again when a new one arrives, and stopped from restoring the old
 * container's offset onto it. One that dies with the container is told nothing.
 * The search text resets for the same reason — it is state, and the state goes.
 */
function FilterMenuContent(props: ContentProps) {
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // State rather than a ref, because the list mounts a commit later than the
  // state that renders it: the effects below have to run when it arrives
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(
    null
  );

  const searchRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const getRows = props.getRows;

  const rows = useMemo(
    () => (props.isLoading === true ? [] : getRows(searchTerm)),
    [props.isLoading, getRows, searchTerm]
  );

  // Heights are held per row rather than handed to the virtualizer's own
  // measureElement: that reports through a path which notifies with the
  // scrolling flag as the sync flag, so a search landing mid-scroll would
  // flushSync out of a lifecycle. A state update lands on the next render
  // instead, and one entry per row means decorations are free to differ.
  const [rowHeights, setRowHeights] = useState<Map<string, number>>(new Map());

  const measureRow = useCallback((node: HTMLDivElement | null) => {
    const key = node?.dataset.rowKey;
    const height = node?.offsetHeight ?? 0;

    if (key === undefined || height === 0) {
      return;
    }

    setRowHeights((previous) => {
      // Compared against what the row is already sized at, not just against a
      // recorded height: every row these menus ship measures at the seed, and
      // storing that anyway re-measures the whole list on each scroll commit
      // that mounts one
      if ((previous.get(key) ?? ESTIMATED_ROW_HEIGHT_PX) === height) {
        return previous;
      }

      return new Map(previous).set(key, height);
    });
  }, []);

  const estimateSize = useCallback(
    (index: number) => {
      const row = rows.at(index);

      if (row === undefined) {
        return ESTIMATED_ROW_HEIGHT_PX;
      }

      // Dividers are chrome with a fixed height; everything else reports the
      // height it actually rendered at, once it has rendered once
      return row.kind === 'divider'
        ? DIVIDER_ITEM_HEIGHT_PX
        : (rowHeights.get(row.key) ?? ESTIMATED_ROW_HEIGHT_PX);
    },
    [rows, rowHeights]
  );

  // A ref, not the element state: refs attach during the commit that mounts
  // the container, so the virtualizer measures on the first paint
  const getScrollElement = useCallback(() => scrollRef.current, []);
  const attachScrollElement = useCallback((node: HTMLDivElement | null) => {
    // The ref keeps the last node: the list unmounts under this component when
    // a refetch swaps it for the skeleton, and handing the virtualizer a null
    // makes it notify from its own layout effect with its scrolling flag as
    // the sync flag. A detached node is inert.
    if (node !== null) {
      scrollRef.current = node;
    }

    setScrollElement(node);
  }, []);

  // Changing with the rows is what invalidates the virtualizer's size and key
  // cache during the render that changes them: it keys that cache on this
  // callback's identity and never on estimateSize, so a stabilized one leaves
  // a search that moves a divider laying out against the previous list.
  const getItemKey = useCallback(
    (index: number) => rows.at(index)?.key ?? String(index),
    [rows]
  );

  const visibleRowCount = Math.ceil(props.maxHeight / ESTIMATED_ROW_HEIGHT_PX);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement,
    estimateSize,
    getItemKey,
    overscan:
      visibleRowCount *
      (props.isFullyOpen
        ? OVERSCAN_VIEWPORTS
        : OVERSCAN_VIEWPORTS_WHILE_OPENING),
    initialRect: { width: props.minWidth, height: props.maxHeight },
  });

  // A newly measured row changes what every row below it sits at, and the
  // virtualizer keys its size cache on the row count and getItemKey, never on
  // estimateSize — so a height arriving is the one thing it cannot see coming
  useEffect(() => {
    virtualizer.measure();
  }, [rowHeights, virtualizer]);

  // A narrowed search leaves the offset past the end of the shorter list, and
  // the virtualizer restores the retained offset onto a container that
  // remounted while a refetch showed the skeleton
  useEffect(() => {
    if (scrollElement === null) {
      return;
    }

    virtualizer.scrollToOffset(0);
  }, [searchTerm, scrollElement, virtualizer]);

  const debouncedSetSearchTerm = useMemo(
    () => debounce((value: string) => setSearchTerm(value), SEARCH_DEBOUNCE_MS),
    []
  );

  useEffect(
    () => () => debouncedSetSearchTerm.cancel(),
    [debouncedSetSearchTerm]
  );

  // The menu stays mounted for its exit transition, which is no longer than the
  // debounce, so a term still pending at close would land during the fade
  useEffect(() => {
    if (!props.isOpen) {
      debouncedSetSearchTerm.cancel();
    }
  }, [props.isOpen, debouncedSetSearchTerm]);

  // Scrolls by index rather than to the container's scrollHeight: that height
  // is the virtualizer's total, which sizes unmounted rows at the estimate, so
  // a jump to it stops short of a list whose rows measure taller. scrollToIndex
  // re-checks the offset after each frame and corrects as those rows mount.
  //
  // Instant, not smooth: that correction loop gives up after ten frames, which
  // a smooth animation cannot land inside.
  const handleScrollToEnd = () => {
    if (rows.length === 0) {
      return;
    }

    virtualizer.scrollToIndex(rows.length - 1, { align: 'end' });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
    debouncedSetSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    debouncedSetSearchTerm.cancel();
    setSearchTerm('');
    // Clearing unmounts the button that was just clicked, and the focus trap
    // pulls the orphaned focus to the menu paper rather than back to the field
    searchRef.current?.focus();
  };

  const handleSearchKeyDown = (event: KeyboardEvent) => {
    // Single characters drive MUI's menu type-ahead, which moves focus off the
    // field and swallows the keystroke. Escape and Tab are longer names, so
    // they still bubble and still close the menu. Modified characters are
    // somebody else's: stopping Cmd+K here would take the global search
    // shortcut with it.
    const isTypeAhead =
      event.key.length === 1 &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.altKey;

    if (MENU_NAVIGATION_KEYS.includes(event.key) || isTypeAhead) {
      event.stopPropagation();
    }
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {props.isSearchEnabled && (
        <Box sx={{ p: 1, minWidth: props.minWidth }}>
          <TextField
            size="small"
            inputRef={searchRef}
            placeholder={props.searchPlaceholder}
            value={searchInput}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchInput !== '' && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    edge="end"
                    aria-label="Clear search"
                    onClick={handleClearSearch}
                  >
                    <CloseRounded fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ width: '100%', marginBottom: 1 }}
          />
        </Box>
      )}

      {props.isLoading === true ? (
        <Box
          sx={{
            p: 2,
            minWidth: props.minWidth,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }}
          >
            <CircularProgress size={20} sx={{ marginRight: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Loading options...
            </Typography>
          </Box>
          {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              height={DENSE_ROW_MIN_HEIGHT_PX}
              sx={{ borderRadius: 1 }}
            />
          ))}
        </Box>
      ) : (
        <div
          ref={attachScrollElement}
          data-testid="filter-menu-list"
          style={{
            maxHeight: props.maxHeight,
            minWidth: props.minWidth,
            overflowY: 'auto',
            position: 'relative',
          }}
        >
          <div
            style={{
              height: virtualizer.getTotalSize(),
              position: 'relative',
              width: '100%',
            }}
          >
            {virtualizer.getVirtualItems().map((virtualItem) => {
              const row = rows.at(virtualItem.index);

              if (row === undefined) {
                return null;
              }

              return (
                // Unsized: the row renders at its natural height and reports
                // it, which is what keeps a taller row from being clipped
                <div
                  key={virtualItem.key}
                  // A divider is a constant, so measuring it stores a height
                  // estimateSize never reads
                  ref={row.kind === 'divider' ? undefined : measureRow}
                  data-index={virtualItem.index}
                  data-row-key={row.key}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualItem.start}px)`,
                    whiteSpace: 'nowrap',
                  }}
                >
                  <FilterMenuRowContent row={row} />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {props.scrollIndicatorLabel !== undefined && props.isLoading !== true && (
        <FilterMenuScrollIndicator
          element={scrollElement}
          label={props.scrollIndicatorLabel}
          onScrollToEnd={handleScrollToEnd}
        />
      )}
    </Box>
  );
}

export function VirtualizedNestedFilterMenu(props: Props) {
  const theme = useTheme();
  const [isFullyOpen, setIsFullyOpen] = useState(false);
  // Counts openings so each gets a fresh content instance. MUI keeps the menu's
  // children mounted through the exit transition, so reopening before it
  // finishes would otherwise reuse the last opening's search text and offset.
  const [openCount, setOpenCount] = useState(0);

  const menu = useDisclosure();
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleMenuOpen = useCallback(() => {
    // Reopening mid-exit cancels the pending exit callback, so this cannot be
    // left to onTransitionExited alone
    setIsFullyOpen(false);
    setOpenCount((previous) => previous + 1);
    menu.open();
  }, [menu]);

  const handleTransitionEntered = useCallback(() => {
    setIsFullyOpen(true);
  }, []);

  const handleTransitionExited = useCallback(() => {
    setIsFullyOpen(false);
  }, []);

  return (
    <>
      <FilterMenuButton
        ref={buttonRef}
        count={props.count}
        onClick={handleMenuOpen}
      >
        {props.label}
      </FilterMenuButton>
      {/* MUI defaults a Menu to transitionDuration="auto", which scales with
          the menu's height — around 340ms for a 400px list, and every bit of
          it is felt as lag between the click and a usable menu. A filter
          dropdown wants the short duration a small surface gets. */}
      <Menu
        open={menu.isOpen}
        anchorEl={buttonRef.current}
        transitionDuration={theme.transitions.duration.shortest}
        onClose={menu.close}
        onTransitionExited={handleTransitionExited}
        TransitionProps={{ onEntered: handleTransitionEntered }}
      >
        <FilterMenuContent
          key={openCount}
          searchPlaceholder={props.searchPlaceholder}
          isSearchEnabled={props.isSearchEnabled ?? true}
          scrollIndicatorLabel={props.scrollIndicatorLabel}
          isLoading={props.isLoading}
          isOpen={menu.isOpen}
          isFullyOpen={isFullyOpen}
          minWidth={props.minWidth ?? DEFAULT_MIN_MENU_WIDTH_PX}
          maxHeight={props.maxHeight ?? DEFAULT_MAX_MENU_HEIGHT_PX}
          getRows={props.getRows}
        />
      </Menu>
    </>
  );
}
